import React from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import GlobalTopBarChrome from 'components/layout/GlobalTopBarChrome';
import { APP_TOP_BAR_HEIGHT_PX } from 'layout/appTopBarLayout';
import { PageGuideProvider } from 'components/pageGuide/PageGuideContext';
import PageGuideCalibrationDialog from 'components/pageGuide/PageGuideCalibrationDialog';
import PageGuideRouteMount from 'components/pageGuide/PageGuideRouteMount';
import { ClinicalHudRefreshSettingsProvider } from 'features/clinicalHud/app/ClinicalHudRefreshSettingsContext';
import { CareStartTtsSettingsProvider } from 'features/carestart/CareStartTtsSettingsContext';
import { shouldHideGlobalTopBarForHud2 } from 'features/clinicalHud2/clinicalHud2IosLayout';
import { shouldHideGlobalTopBarForChartMind7 } from 'features/chartMind7/chartMind7IosLayout';
import { CLINICALHUD_MARKETING_GRADIENT_DARK } from 'features/clinicalHud/marketing/clinicalHudMarketingTokens';
import { useVoxPontImmersiveChrome } from 'features/voxpontShared/voxPontImmersiveChrome';
import { VoxPontImmersiveRestoreBar } from 'features/voxpontShared/VoxPontImmersiveChromeControls';
import { ProductSettingsProvider } from 'components/layout/ProductSettingsContext';
import { useAuth } from 'hooks/useAuth';

/**
 * App shell: fixed global top bar, then scrollable page content with clearance.
 * ClinicalHUD2 on iPhone/iPad uses its own chrome (safe areas + local header).
 * VoxPont interpreter routes on phones hide FusionMSI chrome until restored.
 */
function AppPageShell({ children }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const hideHud2Chrome = shouldHideGlobalTopBarForHud2(pathname);
  const hideChartMind7Chrome = shouldHideGlobalTopBarForChartMind7(pathname);
  const { hideGlobalChrome: hideVoxPontChrome } = useVoxPontImmersiveChrome(pathname);
  const hideProductChrome = hideHud2Chrome || hideChartMind7Chrome;
  const hideSignedOutChrome = !user;
  const hideGlobalChrome = hideSignedOutChrome || hideProductChrome || hideVoxPontChrome;

  return (
    <PageGuideProvider>
      <ClinicalHudRefreshSettingsProvider>
        <CareStartTtsSettingsProvider>
          <ProductSettingsProvider>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100dvh',
                bgcolor: hideProductChrome ? 'transparent' : 'background.default',
                background: hideProductChrome
                  ? CLINICALHUD_MARKETING_GRADIENT_DARK
                  : undefined,
              }}
            >
              {hideSignedOutChrome || hideProductChrome ? null : hideVoxPontChrome ? (
                <VoxPontImmersiveRestoreBar />
              ) : (
                <GlobalTopBarChrome />
              )}
              <Box
                component="main"
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  pt: hideGlobalChrome ? 0 : `${APP_TOP_BAR_HEIGHT_PX}px`,
                }}
              >
                {children}
              </Box>
            </Box>
            {user ? <PageGuideRouteMount /> : null}
            {user ? <PageGuideCalibrationDialog /> : null}
          </ProductSettingsProvider>
        </CareStartTtsSettingsProvider>
      </ClinicalHudRefreshSettingsProvider>
    </PageGuideProvider>
  );
}

export default AppPageShell;
